import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'
import { Stack } from '@mui/material'
import { useMutation, gql } from '@apollo/client'

import { Snackbar } from '@center/ui-kit'
import HeaderExportButton from 'components/global/Buttons/HeaderExportButton'
import TextFilter from '../../components/TextFilter'
import Filters from './Filters/Filters'

import MainContent, { TableContent } from 'components-shared'
import { useRegisterBreadcrumbAction } from 'components/AuthenticatedLayout/Header/components/BreadcrumbActions'
import useFilters from './store'
import useSearch from './use-search'
import Table from './Table/Table'
import { useSites } from 'store'
import { trackEvent } from 'analytics'

const EXPORT_WIRE_LINES = gql`
  mutation ExportWireLines(
    $searchTerm: String
    $provider: String
    $dateFrom: String
    $dateTo: String
    $sfIds: [String]
    $language: String
  ) {
    exportWireLines(
      searchTerm: $searchTerm
      provider: $provider
      dateFrom: $dateFrom
      dateTo: $dateTo
      sfIds: $sfIds
      language: $language
    ) {
      data
      filename
    }
  }
`

const WAN: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [filters, setFilters] = useFilters()
  const apiSearch = useSearch()
  const [{ selected }] = useSites()
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const searchStartedRef = useRef(false)

  const [exportWireLines, { loading: exporting }] = useMutation(EXPORT_WIRE_LINES, {
    onCompleted: (data) => {
      const { data: base64Data, filename } = data.exportWireLines
      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      trackEvent('wan_exported', {
        has_search: Boolean(filters.verbatim && filters.verbatim.length > 2),
        has_provider: Boolean(filters.provider),
        has_date_range: Boolean(filters.dateRange.start || filters.dateRange.end),
        selected_site_count: selected.length
      })
      setShowSuccess(true)
    },
    onError: () => {
      setShowError(true)
    }
  })

  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, verbatim: value }))

    if (value.length > 2 && !searchStartedRef.current) {
      trackEvent('search_wan')
      searchStartedRef.current = true
    } else if (value.length <= 2) {
      searchStartedRef.current = false
    }
  }

  const handleExport = useCallback(() => {
    const sfIds = selected?.map(site => site.sf_id).filter((id): id is string => !!id) || []
    
    const variables: {
      searchTerm?: string
      provider?: string
      dateFrom?: string | null
      dateTo?: string | null
      sfIds: string[]
      language: string
    } = {
      sfIds,
      language: i18n.language || 'fr'
    }

    if (filters.verbatim && filters.verbatim.length > 2) {
      variables.searchTerm = filters.verbatim
    }

    if (filters.provider) {
      variables.provider = filters.provider
    }

    if (filters.dateRange.start) {
      variables.dateFrom = filters.dateRange.start.toISOString()
    }

    if (filters.dateRange.end) {
      variables.dateTo = filters.dateRange.end.toISOString()
    }

    exportWireLines({ variables })
  }, [exportWireLines, filters.dateRange.end, filters.dateRange.start, filters.provider, filters.verbatim, i18n.language, selected])

  const exportAction = useMemo(() => (
    <HeaderExportButton
      analyticsName="wan"
      onClick={handleExport}
      disabled={exporting}
      loading={exporting}
      loadingLabel={t('wan.export.exporting')}
    >
      {t('export.button')}
    </HeaderExportButton>
  ), [exporting, handleExport, t])

  useRegisterBreadcrumbAction(exportAction)

  return (
    <Stack gap={1} flex={1}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Stack
          sx={{
            alignItems: 'center',
            flexDirection: 'row',
            gap: '16px'
          }}
        >
          <TextFilter
            value={filters.verbatim || ''}
            placeholder={t('wan.filters.search')}
            onChange={handleSearchChange}
          />
          <Filters />
        </Stack>
      </Stack>
      <MainContent>
        <TableContent>
          <Table apiSearch={apiSearch} />
        </TableContent>
        <Outlet />
      </MainContent>

      <Snackbar
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={t('wan.export.success')}
        severity="success"
      />

      <Snackbar
        open={showError}
        onClose={() => setShowError(false)}
        message={t('wan.export.error')}
        severity="error"
      />
    </Stack>
  )
}

export default WAN
